import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './AudioseriesCollection.module.css';
import { Play } from 'lucide-react';
import { useUniqueAudiosByCategory } from '../../hooks/useChapters';
import Loading from '../../common/HousGlass Loader/Loading';

function AudioSeriesCollection() {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  const {
    audios,
    isLoading,
    totalPages,
    totalCount
  } = useUniqueAudiosByCategory(id, 'Audio', 'Audio Book', currentPage, itemsPerPage);
  const navigate=useNavigate()

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Create pagination array
  const getPaginationRange = () => {
    const range = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if there are less than maxVisiblePages
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
    } else {
      // Show pages around the current page
      let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      // Adjust start if end is maxed out
      if (end === totalPages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      // Add first page if not included
      if (start > 1) {
        range.push(1);
        if (start > 2) range.push('...');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      
      // Add last page if not included
      if (end < totalPages) {
        if (end < totalPages - 1) range.push('...');
        range.push(totalPages);
      }
    }
    
    return range;
  };

  if (isLoading) {
    return <div className={styles.loadingContainer}><Loading/></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.idDisplay}>
        <h1 className={styles.headTitle}>{id}</h1>
      </div>
      
      <div className={styles.swiperContainer}>
        {/* Grid layout using your existing CSS structure */}
        <div className={styles.audioGrid}>
          {audios && audios.length > 0 ? (
            audios.map((item) => (
              <div key={item.audio_id} className={styles.mediaCard}>
                <div className={styles.imageWrapper} style={{ position: 'relative', height: '100%' }}>
                  <img 
                    src={item.mobile_image_url || "/images/raagprachand.webp"} 
                    alt={item.audio_title}
                    className={styles.mediaImage}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div className={styles.overlay} />
                  <div className={styles.mediaLabel}>
                    <span className={styles.labelText}>{item.category_name}</span>
                  </div>
                  <button className={styles.playButton} onClick={()=>navigate(`/audio-play/${item.audio_slug}`)}>
                    <Play size={20} color="white" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noResults}>No audio series found for this category.</div>
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              className={`${styles.paginationButton} ${styles.prevButton}`}
            >
              Prev
            </button>
            
            {getPaginationRange().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className={styles.ellipsis}>...</span>
              ) : (
                <button
                  key={`page-${page}`}
                  onClick={() => handlePageChange(page)}
                  className={`${styles.paginationButton} ${currentPage === page ? styles.activePage : ''}`}
                >
                  {page}
                </button>
              )
            ))}
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className={`${styles.paginationButton} ${styles.nextButton}`}
            >
              Next
            </button>
          </div>
        )}
        
        {totalCount > 0 && (
          <div className={styles.resultsInfo}>
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} - {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
          </div>
        )}
      </div>
    </div>
  );
}

export default AudioSeriesCollection;